/**
 * Playable characters. Purely cosmetic: the sim, hitboxes and movement are
 * identical for everyone, so a character can never be a gameplay advantage —
 * which also means the choice never has to touch the deterministic sim.
 *
 * The renderer builds a blocky minifig from these numbers (no mesh assets, same
 * philosophy as the synthesised audio), so adding a character is adding a row.
 */

export type AccessoryId =
  | 'cap'
  | 'beanie'
  | 'visor'
  | 'mohawk'
  | 'headphones'
  | 'hood';

export interface CharacterDef {
  id: string;
  name: string;
  /** One-liner shown under the name in the picker. */
  tagline: string;
  /** Colours, one per body part, as hex RGB. */
  skin: number;
  torso: number;
  sleeves: number;
  legs: number;
  /** Accessory + trim colour. */
  accent: number;
  accessory: AccessoryId;
}

export const CHARACTERS: readonly CharacterDef[] = [
  {
    id: 'ace',
    name: 'Ace',
    tagline: 'The standard issue — everyone starts here',
    skin: 0xf0c48c,
    torso: 0x3a76c4,
    sleeves: 0x2c5a94,
    legs: 0x37415c,
    accent: 0xf2f5fa,
    accessory: 'cap',
  },
  {
    id: 'ember',
    name: 'Ember',
    tagline: 'Runs hot, plays close',
    skin: 0x8d5a3a,
    torso: 0xc44536,
    sleeves: 0x8e2f24,
    legs: 0x4c3b34,
    accent: 0xff9c41,
    accessory: 'beanie',
  },
  {
    id: 'volt',
    name: 'Volt',
    tagline: 'Loud hair, louder entrance',
    skin: 0xc98e5a,
    torso: 0xf2c800,
    sleeves: 0x3c3c46,
    legs: 0x3c3c46,
    accent: 0x00e5ff,
    accessory: 'mohawk',
  },
  {
    id: 'frost',
    name: 'Frost',
    tagline: 'Cold hands, colder aim',
    skin: 0xe8cfae,
    torso: 0xdfe8f2,
    sleeves: 0x9db8d4,
    legs: 0x51667f,
    accent: 0x6fd6ff,
    accessory: 'visor',
  },
  {
    id: 'blossom',
    name: 'Blossom',
    tagline: 'Beats in the lobby, beams in the arena',
    skin: 0xf5d0a0,
    torso: 0xe97fb2,
    sleeves: 0xc75d92,
    legs: 0x5c3a52,
    accent: 0xffd1e8,
    accessory: 'headphones',
  },
  {
    id: 'shadow',
    name: 'Shadow',
    tagline: 'You will hear the footsteps too late',
    skin: 0xd9c9c0,
    torso: 0x453b5e,
    sleeves: 0x3a3150,
    legs: 0x2f2842,
    accent: 0x8a5cff,
    accessory: 'hood',
  },
];

/** Everyone who never opens the picker is Ace — nobody renders as a fallback. */
export const DEFAULT_CHARACTER_INDEX = 0;

/** Clamps an untrusted index (wire input, old localStorage) to a real character. */
export const clampCharacterIndex = (index: number): number =>
  Number.isInteger(index) && index >= 0 && index < CHARACTERS.length
    ? index
    : DEFAULT_CHARACTER_INDEX;

export const characterFromIndex = (index: number): CharacterDef =>
  CHARACTERS[clampCharacterIndex(index)]!;
